/**
 * Estado de alcance da internet no web — o que o resto do app chama de
 * "online/offline".
 *
 * Origem: #867 — 2026-10-08 08:43 +0200.
 *
 * Por quê: o web usava `navigator.onLine` cru, e em 2026-10-08 o Chrome
 * (macOS) do owner reportou `false` com a rede boa — token renovado e
 * Firestore respondendo no mesmo instante —, travando os Relatórios (#664).
 * O Android (`core/connectivity/ReachabilityObserver.kt`) e o iOS
 * (`Data/Sources/Connectivity/LiveNetworkStatus.swift`) já tinham resolvido o
 * mesmo problema do lado deles: quem decide é uma **sonda ativa**, e o sinal
 * do sistema é só atalho. Este módulo é a versão web desse desenho.
 *
 * Contrato: `createReachabilityStore(deps)` devolve um store no formato de
 * `useSyncExternalStore` (`subscribe`/`getSnapshot`) e um `kick()` para
 * forçar sonda imediata. Três estados — `UNKNOWN` (ainda não sondou),
 * `ONLINE`, `OFFLINE` —, porque "não sei" não é "online" nem "offline": a UI
 * trata `UNKNOWN` como "não mostra aviso". A sonda só roda enquanto há
 * ouvinte e a aba está visível. Sem efeito colateral além da rede da sonda.
 *
 * Usado por: `useReachability.ts` (binding de navegador, singleton por aba) →
 * `ConnectivityBanner.tsx` (faixa do topo nos três web apps: `apps/app`,
 * `apps/admin`, `apps/crm`), `requiresInternet.ts` (bloqueio de ações) e
 * `apps/app/src/features/reports/useReportRequest.ts`. Movido de
 * `apps/app/src/lib/connectivity/` para o ui-shared em 2026-10-20 (#1010).
 *
 * Arquitetura: núcleo puro — sonda, relógio, visibilidade e eventos chegam por
 * `deps`, para o teste rodar sem rede nem timers reais (mesma separação do
 * `reachabilityFlow` do Android).
 *
 * Falhas conhecidas:
 *  - 2026-10-08 (#664, #867): `navigator.onLine === false` com a rede boa.
 *    Por isso, ao contrário do Android, o evento `offline` do navegador NUNCA
 *    marca offline sozinho — só dispara uma sonda.
 *  - iOS #420: cada tela criava/cancelava o próprio detector e sair de uma
 *    matava o das outras. Aqui há um store só, com contagem de ouvintes.
 *
 * Dicas: os intervalos espelham o Android (5 s online, 3 s offline); a sonda
 * em si está em `useReachability.ts`. Testes em `reachability.test.ts`.
 *
 * Issues: #867 (paridade de conectividade), #664 (Relatórios), #420 (iOS),
 * #1010 (web sempre online: movido para o ui-shared).
 *
 * Histórico:
 *  - 2026-10-20 15:22 +0200 — (#1010) movido de `apps/app` para o ui-shared.
 */

/** Os três estados do detector (`UNKNOWN` = ainda sem resposta da sonda). */
export const Reachability = {
  UNKNOWN: 'UNKNOWN',
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
} as const;
export type Reachability = (typeof Reachability)[keyof typeof Reachability];

/** Intervalo entre sondas depois de um sucesso (Android: `ONLINE_PROBE_INTERVAL_MS`). */
export const ONLINE_PROBE_INTERVAL_MS = 5_000;
/** Intervalo depois de uma falha — mais curto para perceber a volta rápido. */
export const OFFLINE_PROBE_INTERVAL_MS = 3_000;

export type ReachabilityDeps = {
  /** `true` = a internet respondeu. Nunca deve lançar; se lançar, conta como `false`. */
  probe: () => Promise<boolean>;
  /** Agenda `fn` em `ms`; devolve o cancelamento. */
  schedule: (fn: () => void, ms: number) => () => void;
  /** Aba visível? Escondida, a sonda pausa. */
  isVisible: () => boolean;
  /**
   * Liga os gatilhos externos (eventos `online`/`offline`, `visibilitychange`)
   * quando o primeiro ouvinte chega; devolve o desligamento. `onWake` = sondar
   * agora; `onHide` = pausar.
   */
  attach: (onWake: () => void, onHide: () => void) => () => void;
};

export type ReachabilityStore = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => Reachability;
  /** Sonda já, sem esperar o intervalo (ex.: o navegador avisou mudança de rede). */
  kick: () => void;
};

/**
 * Cria o detector. Ver o cabeçalho do módulo para o desenho.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200.
 *
 * Usado por: `useReachability.ts` (instância única do app) e
 * `reachability.test.ts`.
 */
export function createReachabilityStore(deps: ReachabilityDeps): ReachabilityStore {
  let state: Reachability = Reachability.UNKNOWN;
  const listeners = new Set<() => void>();
  let cancelTimer: (() => void) | null = null;
  let detach: (() => void) | null = null;
  let inFlight = false;
  // Muda a cada parada: uma sonda que volta depois do último ouvinte sair (ou
  // de uma pausa) não pode reagendar o laço.
  let generation = 0;

/**
 * Troca o estado e avisa os ouvintes (só se mudou).
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: `run` (resultado da sonda).
 */
  const set = (next: Reachability): void => {
    if (next === state) return;
    state = next;
    for (const l of listeners) l();
  };

/**
 * Cancela o agendamento da próxima sonda.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: `run`, `pause`.
 */
  const clearTimer = (): void => {
    cancelTimer?.();
    cancelTimer = null;
  };

/**
 * Uma rodada: sonda, publica o estado e agenda a próxima (3 s offline / 5 s online).
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: `kick`, `subscribe` e o agendamento interno.
 */
  const run = async (): Promise<void> => {
    if (inFlight || listeners.size === 0 || !deps.isVisible()) return;
    clearTimer();
    inFlight = true;
    const mine = generation;
    let ok: boolean;
    try {
      ok = await deps.probe();
    } catch {
      ok = false;
    }
    inFlight = false;
    if (mine !== generation) return;
    set(ok ? Reachability.ONLINE : Reachability.OFFLINE);
    cancelTimer = deps.schedule(
      () => {
        void run();
      },
      ok ? ONLINE_PROBE_INTERVAL_MS : OFFLINE_PROBE_INTERVAL_MS,
    );
  };

/**
 * Para o laço (último ouvinte saiu ou aba escondida); invalida sonda em voo.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: `subscribe` (desligamento) e o gatilho `onHide`.
 */
  const pause = (): void => {
    generation += 1;
    inFlight = false;
    clearTimer();
  };

/**
 * Sonda já, sem esperar o intervalo.
 *
 * Origem: #867 — 2026-10-08 08:43 +0200 (movido para o ui-shared na #1010).
 *
 * Usado por: `attach` (evento online/offline/visibilidade) e `subscribe`.
 */
  const kick = (): void => {
    void run();
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        detach = deps.attach(kick, pause);
        kick();
      }
      return (): void => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          pause();
          detach?.();
          detach = null;
        }
      };
    },
    // O último estado conhecido sobrevive à saída de todos os ouvintes (como o
    // `replay = 1` do Android): quem chega depois não começa em UNKNOWN.
    getSnapshot: () => state,
    kick,
  };
}
